import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Suspense, lazy } from "react";
import { Toaster } from "./components/ui/sonner";
import { ThemeProvider } from "./context/ThemeContext";
import { AuthProvider } from "./context/AuthContext";
import { SubscriptionProvider } from "./context/SubscriptionContext";
import { HintProvider } from "./components/ContextualHints";
import { BadgeProvider } from "./components/BadgeSystem";
import { InactivityWarning } from "./components/InactivityWarning";
import { CookieConsentBanner } from "./components/CookieConsentBanner";
import PWAInstallPrompt from "./components/PWAInstallPrompt";
import { ConversionPopups } from "./components/ConversionPopups";

// All pages lazy loaded for optimal code splitting
const LandingPage = lazy(() => import("./pages/LandingPage"));
const AuthPage = lazy(() => import("./pages/AuthPage"));
const ForgotPasswordPage = lazy(() => import("./pages/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("./pages/ResetPasswordPage"));
const AcceptInvitePage = lazy(() => import("./pages/AcceptInvitePage"));
const DashboardPage = lazy(() => import("./pages/DashboardPage"));
const MindMapPage = lazy(() => import("./pages/MindMapPage"));
const StudyModePage = lazy(() => import("./pages/StudyModePage"));
const BookClubsPage = lazy(() => import("./pages/BookClubsPage"));
const ClubDetailPage = lazy(() => import("./pages/ClubDetailPage"));
const SharedMindMapPage = lazy(() => import("./pages/SharedMindMapPage"));
const UserGuidePage = lazy(() => import("./pages/UserGuidePage"));
const SubscriptionPage = lazy(() => import("./pages/SubscriptionPage"));
const PrivacyPolicyPage = lazy(() => import("./pages/PrivacyPolicyPage"));
const TermsOfServicePage = lazy(() => import("./pages/TermsOfServicePage"));
const DMCAPage = lazy(() => import("./pages/DMCAPage"));
const BookCatalogPage = lazy(() => import("./pages/BookCatalogPage"));
const BookLandingPage = lazy(() => import("./pages/BookLandingPage"));
const MapsIndexPage = lazy(() => import("./pages/MapsIndexPage"));
const FAQPage = lazy(() => import("./pages/FAQPage"));
const LeaderboardPage = lazy(() => import("./pages/LeaderboardPage"));
const NotificationSettingsPage = lazy(() => import("./pages/NotificationSettingsPage"));
const AdminSecurityPage = lazy(() => import("./pages/AdminSecurityPage"));
const AdminBookCoversPage = lazy(() => import("./pages/AdminBookCoversPage"));
const SettingsPage = lazy(() => import("./pages/SettingsPage"));
const TryNowPage = lazy(() => import("./pages/TryNowPage"));
const GoogleCallbackPage = lazy(() => import("./pages/GoogleCallbackPage"));
const FacebookCallbackPage = lazy(() => import("./pages/FacebookCallbackPage"));

import "./App.css";

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="flex flex-col items-center gap-4">
      <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      <p className="text-muted-foreground text-sm">Loading...</p>
    </div>
  </div>
);

function App() {
  return (
    <HelmetProvider>
      <ThemeProvider>
        <AuthProvider>
          <SubscriptionProvider>
            <BadgeProvider>
              <HintProvider>
                <BrowserRouter>
                  <div className="App min-h-screen bg-background text-foreground relative">
                    {/* Global background texture */}
                    <div className="hero-bg pointer-events-none" />
                    <Suspense fallback={<PageLoader />}>
                      <Routes>
                        <Route path="/" element={<LandingPage />} />
                        <Route path="/auth" element={<AuthPage />} />
                        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                        <Route path="/reset-password" element={<ResetPasswordPage />} />
                        <Route path="/invites/accept" element={<AcceptInvitePage />} />
                        <Route path="/dashboard" element={<DashboardPage />} />
                        <Route path="/mindmap/:bookId" element={<MindMapPage />} />
                        <Route path="/study/:bookId" element={<StudyModePage />} />
                        <Route path="/clubs" element={<BookClubsPage />} />
                        <Route path="/clubs/:clubId" element={<ClubDetailPage />} />
                        <Route path="/shared/:shareId" element={<SharedMindMapPage />} />
                        <Route path="/guide" element={<UserGuidePage />} />
                        <Route path="/privacy" element={<PrivacyPolicyPage />} />
                        <Route path="/terms" element={<TermsOfServicePage />} />
                        <Route path="/dmca" element={<DMCAPage />} />
                        <Route path="/faq" element={<FAQPage />} />
                        <Route path="/catalog" element={<BookCatalogPage />} />
                        <Route path="/maps" element={<MapsIndexPage />} />
                        <Route path="/maps/:slug" element={<BookLandingPage />} />
                        <Route path="/leaderboard" element={<LeaderboardPage />} />
                        <Route path="/notifications" element={<NotificationSettingsPage />} />
                        <Route path="/settings" element={<SettingsPage />} />
                        <Route path="/try" element={<TryNowPage />} />
                        <Route path="/auth/google/callback" element={<GoogleCallbackPage />} />
                        <Route path="/auth/facebook/callback" element={<FacebookCallbackPage />} />
                        <Route path="/subscription" element={<SubscriptionPage />} />
                        <Route path="/subscription/success" element={<SubscriptionPage />} />
                        <Route path="/subscription/cancel" element={<SubscriptionPage />} />
                        <Route path="/admin/security" element={<AdminSecurityPage />} />
                        <Route path="/admin/book-covers" element={<AdminBookCoversPage />} />
                      </Routes>
                    </Suspense>
                    <InactivityWarning />
                    <CookieConsentBanner />
                    <ConversionPopups />
                    <Toaster position="bottom-right" />
                    <PWAInstallPrompt />
                  </div>
                </BrowserRouter>
              </HintProvider>
            </BadgeProvider>
          </SubscriptionProvider>
        </AuthProvider>
      </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
